import moment from 'moment-timezone';
import 'moment/locale/es';
import { parse, format } from 'date-fns';
import { es } from 'date-fns/locale';
moment.locale('es');
const USER_TZ = 'America/Mexico_City';

// ---------- Conversores básicos ----------
export const toIso = (anyDateStr) =>
    moment(anyDateStr, ['LL', 'YYYY-MM-DD']).format('YYYY-MM-DD'); // → "YYYY-MM-DD"

export const isoToLL = (iso) => formatMX(iso); // "D de MMMM de YYYY" en MX

export const llToIso = (ll) => moment(ll, 'LL').format('YYYY-MM-DD');

export const dateObjToIso = (d) => moment(d).format('YYYY-MM-DD');

export const isoToDateObj = (iso) => moment(iso, 'YYYY-MM-DD').toDate();

// ---------- Formatos de salida ----------
/** Devuelve una fecha corta "D/M/YYYY". Acepta Date, ISO, ISO extendido y "LL". */
export const toShortDMY = (input) => {
    if (!input) return '';
    let m = moment(input, [moment.ISO_8601, 'YYYY-MM-DD', 'LL', 'L', 'D/M/YYYY'], true);
    if (!m.isValid()) m = moment(input); // fallback
    if (!m.isValid()) return String(input);
    return m.format('D/M/YYYY');
};

/** "7 de agosto de 2025" -> "2025-08-07" (clave ordenable/deduplicable) */
export const normalizeDateKey = (d) => {
    const months = {
        enero: '01', febrero: '02', marzo: '03', abril: '04', mayo: '05', junio: '06',
        julio: '07', agosto: '08', septiembre: '09', octubre: '10', noviembre: '11', diciembre: '12',
    };
    const txt = String(d).trim().toLowerCase();
    const m = txt.match(
        /(\d{1,2})\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+de\s+(\d{4})/
    );
    if (!m) return txt;
    const day = m[1].padStart(2, '0');
    const month = months[m[2]];
    const year = m[3];
    return `${year}-${month}-${day}`;
};

/** Recibe string o array de fechas y devuelve únicas conservando el orden original */
export const formatUniqueDates = (value) => {
    const arr = Array.isArray(value)
        ? value
        : String(value).split(',').map((s) => s.trim()).filter(Boolean);

    const seen = new Set();
    const unique = [];
    for (const d of arr) {
        const key = normalizeDateKey(d);
        if (!seen.has(key)) {
            seen.add(key);
            unique.push(d); // conserva el texto original para mostrar
        }
    }
    return unique.join(', ');
};

// ---------- Normalización de entrada libre ----------
/** Intenta convertir varios formatos comunes a "YYYY-MM-DD". Devuelve "" si falla. */
export const toIsoSafe = (s) => {
    if (!s) return '';
    const norm = String(s).replaceAll(/[.\s/_]/g, '-');

    // 1) Ya es ISO
    if (/^\d{4}-\d{2}-\d{2}$/.test(norm)) return norm;

    // 2) DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
        const d = parse(s, 'dd/MM/yyyy', new Date());
        return Number.isNaN(d?.getTime()) ? '' : format(d, 'yyyy-MM-dd');
    }

    // 2.1) DD-MM-YYYY
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(norm)) {
        const d = parse(norm, 'd-M-yyyy', new Date());
        return Number.isNaN(d?.getTime()) ? '' : format(d, 'yyyy-MM-dd');
    }

    // 3) Largo en español: "[miércoles, ]01 de octubre de 2025"
    try {
        const sinDiaSemana = String(s).replace(/^[a-záéíóúñ]+,\s*/i, '');
        const d = parse(sinDiaSemana, "dd 'de' MMMM 'de' yyyy", new Date(), { locale: es });
        return Number.isNaN(d?.getTime()) ? '' : format(d, 'yyyy-MM-dd');
    } catch {
        return '';
    }
};

// ---------- Helpers de visualización coherentes (UTC -> MX) ----------
export function labelOutDate(date, fallback = 'Sin salida') {
    return date ? isoToLL(date) : fallback;
}

/** ISO/UTC → string largo MX ("D de MMMM de YYYY") */
export const formatMX = (iso) => {
    if (!iso) return 'Sin fecha';
    return moment.utc(iso).tz(USER_TZ).format('D [de] MMMM [de] YYYY');
};

/** Si ya viene humano, úsalo; si es ISO/Date, formatea a MX. */
export const displayDate = (value, fallback = 'Sin fecha') => {
    if (!value) return fallback;
    const s = String(value).trim();

    // 1) Si ya viene un texto humano en español, respétalo
    if (/de\s+(enero|febrero|...|diciembre)\s+de/i.test(s)) return s;

    // 2) "YYYY-MM-DD" (fecha sin hora): formatear en TZ local sin pasar por UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return moment.tz(s, 'YYYY-MM-DD', USER_TZ).format('D [de] MMMM [de] YYYY');
    }

    // 3) ISO con tiempo (contiene 'T'): ahí sí, trátalo como UTC → TZ local
    if (s.includes('T')) {
        return moment.utc(s).tz(USER_TZ).format('D [de] MMMM [de] YYYY');
    }

    // 4) Último recurso
    const m = moment(s);
    return m.isValid() ? m.tz(USER_TZ).format('D [de] MMMM [de] YYYY') : s;
};

export const displayOutDate = (value) => (value ? displayDate(value) : 'Sin salida');